import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Router, RouterLink, RouterLinkActive, ActivatedRoute } from '@angular/router';
import { DialogCloseResult, DialogModule, DialogService } from '@progress/kendo-angular-dialog';
import { NotificationModule } from '@progress/kendo-angular-notification';
import { InactiveAlertComponent } from '../inactive-alert/inactive-alert.component';
import { LoggerService } from '../../shared/logger.service';
import { ApiService } from '../../api/api.service';
import { AuthTokenService } from '../auth-token.service';
import { ToastrService } from 'ngx-toastr';
import { UserDataService } from '../../users/user-data.service';
import { CasiCloseOptions } from '../../common/constants.lib';
import { AuthService } from '../auth.service';
import { SettingsService } from '../../settings/settings.service';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { all } from '@awesome.me/kit-787edf9aa0/icons';
import { AutoFocusDirective } from '../../directives/auto-focus.directive';
import { TimeoutService } from '../timeout.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatSlideToggleModule,
    NotificationModule,
    RouterLink,
    RouterLinkActive,
    DialogModule,
    InactiveAlertComponent,
    FontAwesomeModule,
    AutoFocusDirective,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent implements OnInit, OnDestroy {

  loginForm: FormGroup;
  busy: boolean = false;
  @ViewChild("appendTo", { read: ViewContainerRef }) appendTo: ViewContainerRef | undefined;
  private returnUrl: string | null = null;

  private _subs: Subscription[] = [];

  constructor(
    private logger: LoggerService,
    private fb: NonNullableFormBuilder,
    private apiService: ApiService,
    private authTokenService: AuthTokenService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private dialogService: DialogService,
    private userDataService: UserDataService,
    private settings: SettingsService,
    public faIcons: FaIconLibrary,
    private timeoutService: TimeoutService
  ) {
    faIcons.addIcons(...all);

    this.loginForm = this.fb.group({
      email: [this.settings.lastLogin, [Validators.required, Validators.email]],
      password: ['', [Validators.required]]
    });

  }

  //#region Forms

  get f() {
    return this.loginForm.controls;
  }

  get email() {
    return this.loginForm.get('email');
  }
  get password() {
    return this.loginForm.get('password');
  }

  //#endregion

  //#region Ng Events

  ngOnInit(): void {
    this._subs.push(this.route.queryParams.subscribe(params => {
      if (params['returnUrl'])
        this.returnUrl = params['returnUrl'];
    }));
  }

  ngOnDestroy(): void {
    this.logger.debug('ngOnDestroy...');
    this._subs.forEach(s => s.unsubscribe());
  }

  //#endregion

  onSubmit() {
    this.logger.debug('onSubmit...', this.loginForm.value);
    this.busy = true;
    const { email, password } = this.loginForm.value;
    this.settings.lastLogin = email;
    this.authService
      .login(email, password)
      .then(resData => {
        this.logger.debug('onSubmit:resData: ', resData);
        this.authTokenService.setToken(resData);
        // this.authTokenService.loggedIn = this.authTokenService.isLoggedIn
        this.apiService.getCsrfAsPromise();
        this.loginForm.reset();
        // window.electronAPI.apiLog(`Login success for ${email}!`);
        // this.timeoutService.startCountdown();
        if (this.returnUrl)
          this.router.navigate([this.returnUrl]);
        else
          this.router.navigate(['/home']);
      })
      .catch(err => {
        this.logger.error('onSubmit: error: ', err);
        if (err.error.toLowerCase().indexOf("incorrect password") !== -1 || err.error.toLowerCase().indexOf("not found") != -1) {
          this.toastr.error('Invalid email/password combination.', 'Login', {
            positionClass: 'toast-center-center'
          });
        } else if (err.error.toLowerCase().indexOf("inactive") !== -1) {
          this.openInactiveAlert();
        }
      })
      .finally(() => this.busy = false);
  }

  private openInactiveAlert(): void {
    const dialogRef = this.dialogService.open({
      content: InactiveAlertComponent,
      minWidth: 320,
      // width: 450,
      // cssClass: "custom-css-class"
    });

    dialogRef.result.subscribe((r: any) => {
      this.logger.debug('openInactiveAlert:result:', { r, instance: r instanceof DialogCloseResult });
      if (r instanceof DialogCloseResult) {
        return;
      }
      if (r.text === CasiCloseOptions.Ok) {
        this.logger.debug("openInactiveAlert:ok... ");
        return;
      }
      if (r.text === CasiCloseOptions.Navigate) {
        this.logger.debug(`openInactiveAlert:navigate...`);
        this.router.navigate(['/contact-us']);
        return;
      }
    });

  }
}
